/**
 * 저장된 보기 · 열 숨기기 (CRM/ERP 목록 공용). 둘 다 사용자 설정 API
 * (`/api/prefs`) 에 JSON 한 건으로 저장하고, 페이지가 `#prefsData` 로 내보낸
 * 값을 첫 렌더에 바로 적용한다(추가 요청 없음).
 *
 *  setupSavedViews(toolbar, {key, params, apply})
 *    '보기 ▾' 버튼(현재 보기 저장 · 기본으로 설정 · 이름 바꾸기 · 삭제) + 내 보기
 *    칩 행(.cal-chip-f, 현재 쿼리와 같은 보기가 활성). 파라미터 없이 들어왔고
 *    기본 보기가 있으면 history.replaceState 뒤 calendar-spa navigate() 로
 *    부분 로드한다 — 서버 리다이렉트 없음.
 *    문서: {"views":[{"id","name","q","def"}]}  (q = 허용 파라미터만, 키 정렬)
 *
 *  setupColumnHiding(list, {key, toolbar})
 *    헤더(.rec-th) 우클릭 '열 숨기기' → data-col-<key> 를 가진 셀·헤더에
 *    .col-hidden. 툴바 '열 ▾' 메뉴에서 복원. 문서: ["col", …]
 *
 * SPA 부분 교체(툴바 innerHTML 교체, #recList 제자리 교체) 뒤에도 살아남도록
 * 모든 DOM 참조는 매번 id/셀렉터로 다시 찾고 `calendar:navigated` 에 다시 그린다.
 */
import { showContextMenu, closeContextMenu } from './context-menu.js';
import { appConfirm } from './confirm-dialog.js';
import { navigate } from './calendar-spa.js';
import { showToast } from './calendar-history.js';

const NAME_MAX = 40;

const LABELS = {
    ko: {
        views: '보기', save: '현재 보기 저장…', setDefault: '기본으로 설정', unsetDefault: '기본 해제',
        rename: '이름 바꾸기…', del: '삭제', defaultTag: '기본', namePh: '보기 이름', ok: '저장', cancel: '취소',
        deleteQ: '보기 "%s"를 삭제할까요?', noViews: '저장된 보기가 없습니다',
        cols: '열', hideCol: '열 숨기기', showCol: '표시: %s', showAll: '모든 열 표시',
        hint: '헤더 우클릭 → 열 숨기기', failed: '저장하지 못했습니다',
    },
    en: {
        views: 'Views', save: 'Save current view…', setDefault: 'Set as default', unsetDefault: 'Clear default',
        rename: 'Rename…', del: 'Delete', defaultTag: 'default', namePh: 'View name', ok: 'Save', cancel: 'Cancel',
        deleteQ: 'Delete view "%s"?', noViews: 'No saved views',
        cols: 'Columns', hideCol: 'Hide column', showCol: 'Show: %s', showAll: 'Show all columns',
        hint: 'Right-click a header → Hide column', failed: 'Could not save',
    },
};

function labels(el) {
    const lang = (document.documentElement.lang || 'ko').toLowerCase();
    const base = LABELS[lang.startsWith('en') ? 'en' : 'ko'];
    // data-l-* on the host element overrides the fallback strings.
    const ds = (el && el.dataset) || {};
    const out = {};
    Object.keys(base).forEach((k) => {
        const dk = 'l' + k.charAt(0).toUpperCase() + k.slice(1);
        out[k] = ds[dk] || base[k];
    });
    return out;
}

function csrf() {
    if (window.getCSRFToken) return window.getCSRFToken();
    const meta = document.querySelector('meta[name="csrf-token"]');
    if (meta && meta.content) return meta.content;
    const m = document.cookie.match(/(?:^|;\s*)_csrf=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : '';
}

// ── prefs transport ────────────────────────────────────────────────────
let prefsCache = null;
let prefsEl = null;

/** #prefsData (map key → raw value string) parsed once per page (re-parsed after an SPA page swap replaces the element). */
export function pagePrefs() {
    const el = document.getElementById('prefsData');
    if (prefsCache && el === prefsEl) return prefsCache;
    prefsCache = {};
    prefsEl = el;
    if (el) {
        try { prefsCache = JSON.parse(el.textContent || '{}') || {}; } catch (_) { prefsCache = {}; }
    }
    return prefsCache;
}

/** POST /api/prefs — an empty value deletes the key. Resolves to {key,value}. */
export function savePref(key, value) {
    const body = new URLSearchParams({ key, value: value == null ? '' : String(value), _csrf: csrf() });
    return fetch('/api/prefs', {
        method: 'POST', body, credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-CSRF-Token': csrf() },
    }).then((r) => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then((d) => { pagePrefs()[key] = d.value || ''; return d; });
}

/** GET /api/prefs?key= → value string ("" when unset). */
export function loadPref(key) {
    return fetch('/api/prefs?key=' + encodeURIComponent(key), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((r) => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then((d) => d.value || '');
}

function readJSON(key, fallback) {
    const raw = pagePrefs()[key];
    if (!raw) return fallback;
    try { const v = JSON.parse(raw); return v == null ? fallback : v; } catch (_) { return fallback; }
}

// 피드백은 공용 토스트 하나로 (spec 2.1.12): 오류는 .cal-undo-toast.err
function toast(msg, err) { showToast(msg, { error: !!err }); }

// ── query canonicalisation ─────────────────────────────────────────────
/** Allowed params only, empties dropped, keys sorted → stable string for comparison. */
export function canonicalQuery(search, params) {
    const sp = new URLSearchParams(search || '');
    const keys = Array.from(new Set(Array.from(sp.keys()))).filter((k) => !params || params.includes(k)).sort();
    const out = new URLSearchParams();
    keys.forEach((k) => sp.getAll(k).filter((v) => v !== '').forEach((v) => out.append(k, v)));
    return out.toString();
}

function uid() { return 'v' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6); }

// Small anchored name box (in-app; native prompt() is not used anywhere).
function askName(anchor, L, initial) {
    return new Promise((resolve) => {
        closeContextMenu();
        const box = document.createElement('div');
        box.className = 'ctx-menu sv-namebox';
        box.style.padding = '8px';
        box.innerHTML = '<input class="form-input" type="text" maxlength="' + NAME_MAX + '" style="width:220px; margin-bottom:6px;">'
            + '<div style="display:flex; gap:6px; justify-content:flex-end;"><button type="button" class="btn btn-secondary btn-sm sv-cancel"></button><button type="button" class="btn btn-primary btn-sm sv-ok"></button></div>';
        const input = box.querySelector('input');
        const ok = box.querySelector('.sv-ok');
        const cancel = box.querySelector('.sv-cancel');
        input.placeholder = L.namePh; input.value = initial || '';
        ok.textContent = L.ok; cancel.textContent = L.cancel;
        let done = false;
        function finish(v) {
            if (done) return; done = true;
            box.remove();
            document.removeEventListener('mousedown', outside, true);
            resolve(v);
        }
        function outside(e) { if (!box.contains(e.target)) finish(null); }
        function submit() {
            const v = input.value.trim().slice(0, NAME_MAX);
            if (!v) { input.focus(); return; }
            finish(v);
        }
        ok.addEventListener('click', submit);
        cancel.addEventListener('click', () => finish(null));
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') { e.preventDefault(); submit(); }
            else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); finish(null); }
        });
        document.body.appendChild(box);
        const r = anchor ? anchor.getBoundingClientRect() : { left: 20, bottom: 20 };
        const br = box.getBoundingClientRect();
        box.style.left = Math.max(4, Math.min(r.left, window.innerWidth - br.width - 8)) + 'px';
        box.style.top = Math.max(4, Math.min(r.bottom + 4, window.innerHeight - br.height - 8)) + 'px';
        setTimeout(() => document.addEventListener('mousedown', outside, true), 0);
        input.focus(); input.select();
    });
}

// ── saved views ────────────────────────────────────────────────────────
/**
 * @param {HTMLElement} toolbar   where the '보기 ▾' button goes (its innerHTML may be region-swapped)
 * @param {{key:string, params?:string[], apply?:(query:string)=>void, chipsAfter?:HTMLElement}} opts
 * @returns {{views:()=>object[], render:()=>void}}
 */
export function setupSavedViews(toolbar, opts) {
    opts = opts || {};
    const key = opts.key;
    if (!toolbar || !key) return null;
    const params = Array.isArray(opts.params) && opts.params.length ? opts.params : null;
    const L = labels(toolbar);
    const toolbarSel = toolbar.id ? '#' + toolbar.id : null;
    const doc = readJSON(key, {});
    let views = Array.isArray(doc.views) ? doc.views.filter((v) => v && v.id && v.name) : [];

    function tb() { return (toolbarSel && document.querySelector(toolbarSel)) || (toolbar.isConnected ? toolbar : null); }
    function current() { return canonicalQuery(window.location.search, params); }
    function active() { const q = current(); return views.find((v) => (v.q || '') === q) || null; }
    function persist() {
        const value = views.length ? JSON.stringify({ views }) : '';
        return savePref(key, value).catch(() => toast(L.failed, true));
    }
    function apply(q) {
        if (typeof opts.apply === 'function') { opts.apply(q); return; }
        navigate(window.location.pathname + (q ? '?' + q : ''));
    }

    // chip row lives outside any [data-spa-region] so it survives swaps
    let row = document.querySelector('.sv-chips[data-key="' + key + '"]');
    if (!row) {
        row = document.createElement('div');
        row.className = 'cal-list-chips sv-chips';
        row.dataset.key = key;
        const after = opts.chipsAfter || toolbar.closest('.crm-head, .erp-head, .cal-head') || toolbar;
        after.insertAdjacentElement('afterend', row);
    }

    function viewMenu(v, x, y) {
        showContextMenu(x, y, [
            { label: v.def ? L.unsetDefault : L.setDefault, onClick: () => { const on = !v.def; views.forEach((o) => { o.def = false; }); v.def = on; persist().then(render); } },
            { label: L.rename, onClick: () => askName(null, L, v.name).then((n) => { if (n && n !== v.name) { v.name = n; persist().then(render); } }) },
            { separator: true },
            { label: L.del, danger: true, onClick: () => appConfirm({ message: L.deleteQ.replace('%s', v.name), okLabel: L.del, danger: true }).then((ok) => {
                if (!ok) return;
                views = views.filter((o) => o !== v);
                persist().then(render);
            }) },
        ]);
    }

    function render() {
        const host = tb();
        if (host && !host.querySelector('.sv-btn')) {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'btn btn-secondary btn-sm sv-btn';
            b.textContent = L.views + ' ▾';
            b.addEventListener('click', (e) => {
                e.preventDefault(); e.stopPropagation();
                const r = b.getBoundingClientRect();
                const cur = active();
                const items = [{ label: L.save, onClick: () => askName(b, L, cur ? cur.name : '').then((n) => {
                    if (!n) return;
                    const q = current();
                    const dup = views.find((v) => v.name === n);
                    if (dup) dup.q = q; else views.push({ id: uid(), name: n, q, def: false });
                    persist().then(render);
                }) }];
                items.push({ separator: true });
                if (!views.length) items.push({ label: L.noViews, disabled: true });
                views.forEach((v) => items.push({ label: (v.def ? '★ ' : '') + v.name, hint: v === cur ? '✓' : '', onClick: () => apply(v.q || '') }));
                if (cur) {
                    items.push({ separator: true });
                    items.push({ label: cur.def ? L.unsetDefault : L.setDefault, onClick: () => { const on = !cur.def; views.forEach((o) => { o.def = false; }); cur.def = on; persist().then(render); } });
                    items.push({ label: L.rename, onClick: () => askName(b, L, cur.name).then((n) => { if (n && n !== cur.name) { cur.name = n; persist().then(render); } }) });
                    items.push({ label: L.del, danger: true, onClick: () => appConfirm({ message: L.deleteQ.replace('%s', cur.name), okLabel: L.del, danger: true }).then((ok) => {
                        if (!ok) return;
                        views = views.filter((o) => o !== cur);
                        persist().then(render);
                    }) });
                }
                showContextMenu(r.left, r.bottom + 4, items);
            });
            host.appendChild(b);
        }
        // chips
        row.innerHTML = '';
        row.hidden = !views.length;
        if (views.length) {
            const label = document.createElement('span');
            label.className = 'text-muted'; label.textContent = L.views;
            row.appendChild(label);
        }
        const q = current();
        views.forEach((v) => {
            const a = document.createElement('a');
            a.className = 'cal-chip-f sv-chip' + ((v.q || '') === q ? ' active' : '');
            a.href = window.location.pathname + (v.q ? '?' + v.q : '');
            a.dataset.viewId = v.id;
            a.textContent = v.name;
            if (v.def) { const b = document.createElement('b'); b.textContent = L.defaultTag; a.appendChild(b); }
            a.addEventListener('click', (e) => { e.preventDefault(); apply(v.q || ''); });
            a.addEventListener('contextmenu', (e) => { e.preventDefault(); e.stopPropagation(); viewMenu(v, e.clientX, e.clientY); });
            row.appendChild(a);
        });
    }

    render();
    document.addEventListener('calendar:navigated', render, { signal: opts.signal });

    // Entered without any params and a default view exists → apply it client-side.
    const def = views.find((v) => v.def && v.q);
    if (def && !window.location.search) {
        const url = window.location.pathname + '?' + def.q;
        window.history.replaceState({ spa: true }, '', url);
        if (typeof opts.apply === 'function') opts.apply(def.q);
        else navigate(url, { replace: true, keepScroll: true });
    }

    return { views: () => views.slice(), render };
}

// ── column hiding ──────────────────────────────────────────────────────
// A column is named by a `data-col-<key>` attribute, or by a namespaced `data-col` *value*
// (`data-col="prop:<id>"` — html/template cannot emit dynamic attribute names for custom properties).
const COL_KEY = /^[A-Za-z0-9_-]+(:[A-Za-z0-9_-]+)?$/;
function colOf(el) {
    if (!el || !el.attributes) return null;
    for (let i = 0; i < el.attributes.length; i += 1) {
        const n = el.attributes[i].name;
        if (n.startsWith('data-col-') && n.length > 9) return n.slice(9);
    }
    const v = el.getAttribute('data-col') || '';
    return v.includes(':') && COL_KEY.test(v) ? v : null;
}
function colSelector(col) { return col.includes(':') ? '[data-col="' + col + '"]' : '[data-col-' + col + ']'; }
function headerLabel(root, col) {
    const th = root && root.querySelector('.rec-th' + colSelector(col));
    if (!th) return col;
    let t = '';
    th.childNodes.forEach((n) => { if (n.nodeType === 3) t += n.textContent; else if (n.tagName !== 'I') t += n.textContent; });
    return t.trim() || col;
}

/**
 * @param {HTMLElement} list  the list root (e.g. #recList; re-found by id after SPA swaps)
 * @param {{key:string, toolbar?:HTMLElement}} opts
 * @returns {{hide:(col:string)=>void, show:(col:string)=>void, showAll:()=>void, hidden:()=>string[], items:(th:Element)=>object[], apply:()=>void}}
 */
export function setupColumnHiding(list, opts) {
    opts = opts || {};
    const key = opts.key;
    if (!list || !key) return null;
    const listId = list.id;
    const L = labels(list);
    const toolbar = opts.toolbar || document.querySelector('.crm-tools, .erp-tools, .cal-list-tools');
    const toolbarSel = toolbar && toolbar.id ? '#' + toolbar.id : null;
    let hidden = readJSON(key, []);
    if (!Array.isArray(hidden)) hidden = [];
    hidden = hidden.filter((c) => typeof c === 'string' && COL_KEY.test(c));
    const sig = opts.signal;

    function root() { return (listId && document.getElementById(listId)) || (list.isConnected ? list : null); }
    function tb() { return (toolbarSel && document.querySelector(toolbarSel)) || (toolbar && toolbar.isConnected ? toolbar : null); }
    function persist() { return savePref(key, hidden.length ? JSON.stringify(hidden) : '').catch(() => toast(L.failed, true)); }

    function apply() {
        const r = root();
        if (!r) return;
        r.querySelectorAll('.col-hidden').forEach((el) => { if (colOf(el)) el.classList.remove('col-hidden'); });
        hidden.forEach((c) => r.querySelectorAll(colSelector(c)).forEach((el) => el.classList.add('col-hidden')));
        r.classList.toggle('has-hidden-cols', hidden.length > 0);
        r.colHiding = api;
        renderButton();
    }
    function hide(col) { if (!col || hidden.includes(col)) return; hidden.push(col); persist(); apply(); }
    function show(col) { if (!hidden.includes(col)) return; hidden = hidden.filter((c) => c !== col); persist(); apply(); }
    function showAll() { if (!hidden.length) return; hidden = []; persist(); apply(); }
    /** Menu items a page can splice into its own header context menu. */
    function items(th) {
        const col = colOf(th);
        return col && !hidden.includes(col) ? [{ label: L.hideCol, onClick: () => hide(col) }] : [];
    }

    function renderButton() {
        const host = tb();
        if (!host || host.querySelector('.sv-cols-btn')) return;
        if (!document.querySelector('#recList .rec-head .rec-th')) return; // 숨길 열 헤더가 없는 페이지(/crm/today)엔 버튼도 없다 (QA ux-14)
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'btn btn-ghost btn-sm sv-cols-btn';
        b.textContent = L.cols + ' ▾';
        b.addEventListener('click', (e) => {
            e.preventDefault(); e.stopPropagation();
            const r = b.getBoundingClientRect();
            const r2 = root(); // 이 클로저 안에 이미 있는 root() (saved-views.js:325)
            // 체크리스트 (§9.5): 목록 헤더의 열을 그대로 훑어 ☑/☐ 로 낸다.
            // 우클릭 경로(items(th))와 같은 hide/show 를 부르므로 저장도 같다.
            const cols = [];
            if (r2) r2.querySelectorAll('.rec-head .rec-th').forEach((th) => { const c = colOf(th); if (c && !cols.includes(c)) cols.push(c); });
            const menu = cols.map((c) => {
                const off = hidden.includes(c);
                return { label: (off ? '☐ ' : '☑ ') + headerLabel(r2, c), onClick: () => (off ? show(c) : hide(c)) };
            });
            if (!menu.length) return; // renderButton only exists where a hideable header does (B12)
            if (hidden.length) { menu.push({ separator: true }); menu.push({ label: L.showAll, onClick: showAll }); }
            showContextMenu(r.left, r.bottom + 4, menu);
        });
        host.appendChild(b);
    }

    // Header right-click. Delegated at document level so it survives root
    // swaps; a page handler that already showed its own menu (defaultPrevented)
    // wins — it can merge items() into that menu instead.
    document.addEventListener('contextmenu', (e) => {
        const r = root();
        if (!r || e.defaultPrevented) return;
        const th = e.target.closest && e.target.closest('.rec-th');
        if (!th || !r.contains(th)) return;
        const its = items(th);
        if (!its.length) return;
        e.preventDefault();
        showContextMenu(e.clientX, e.clientY, its);
    }, { signal: sig });

    const api = { hide, show, showAll, hidden: () => hidden.slice(), items, apply };
    apply();
    document.addEventListener('calendar:navigated', apply, { signal: sig });
    return api;
}
